import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import DailyMacroTracker from '@/components/DailyMacroTracker';
import SwapFoodDialog from '@/components/SwapFoodDialog';
import RecipeLibraryModal from '@/components/RecipeLibraryModal';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  FaArrowLeft, FaAppleAlt, FaClock, FaDownload, FaFire, FaPills, 
  FaUtensils, FaDumbbell, FaBed, FaStickyNote, FaFileAlt, FaHeartbeat, 
  FaShieldAlt, FaBan, FaLightbulb, FaClipboardList, FaExchangeAlt,
  FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import { toast } from 'sonner';
import { getFoodEmoji } from '../utils/foodEmoji';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIET_TAB_LABELS = {
  'training_inferior': { name: 'Dia Inferior', icon: FaDumbbell, color: 'var(--brand-primary)' },
  'training_superior': { name: 'Dia Superior', icon: FaDumbbell, color: '#00F0FF' },
  'descanso': { name: 'Dia Descanso', icon: FaBed, color: '#CCFF00' },
  'lineal': { name: 'Dieta Lineal', icon: FaUtensils, color: '#FF69B4' }
};

// ========== SUB-COMPONENTS ==========

const SectionToggle = ({ title, icon: Icon, iconColor, children, defaultOpen = false, count, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <button data-testid={testId} onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors">
        <div className="flex items-center gap-3">
          <Icon style={{ color: iconColor }} className="text-lg flex-shrink-0" />
          <span className="text-white font-semibold text-sm uppercase tracking-wide">{title}</span>
          {count > 0 && (
            <Badge className="text-xs px-2 py-0 border-0" style={{ background: `${iconColor}30`, color: iconColor }}>{count}</Badge>
          )}
        </div>
        {open ? <FaChevronUp className="text-gray-500" /> : <FaChevronDown className="text-gray-500" />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

const MealCard = ({ meal, mealIndex, isPostWorkout, showPreWorkoutSupps, preWorkoutSupps, activeDietTab, selectedOptions, setSelectedOptions, extrasPerMeal, isConsumed, onToggleConsumed, swapsMap, onSwapItem, compIn, onDistributeDeficit, hasActiveDistribution, onUndoDistribution, canUseRecipeLibrary, onOpenRecipeLibrary }) => {
  const hasMultipleOptions = meal.options && Array.isArray(meal.options) && meal.options.length > 1;
  const numOptions = meal.options?.length || meal.num_options || 1;
  const selectedOption = selectedOptions[mealIndex] || 0;
  
  let itemsToShow = [];
  // Buscar items en cascada, usando siempre la primera fuente NO VACÍA
  // (un array vacío también es truthy en JS, por eso hay que validar length > 0).
  if (meal.options?.length > 0 && meal.options[selectedOption]?.items?.length > 0) {
    itemsToShow = meal.options[selectedOption].items;
  } else if (meal.options?.length > 0) {
    // Buscar la PRIMERA opción con items (en lugar de solo la 0)
    const firstOptWithItems = meal.options.find(o => o?.items?.length > 0);
    if (firstOptWithItems) itemsToShow = firstOptWithItems.items;
  }
  // Si aún no hay items, caer a items/foods/alimentos directos del meal
  if (itemsToShow.length === 0) {
    if (meal.items?.length > 0) itemsToShow = meal.items;
    else if (meal.foods?.length > 0) itemsToShow = meal.foods;
    else if (meal.alimentos?.length > 0) itemsToShow = meal.alimentos;
  }

  // ===== Macronutrientes por comida =====
  // Prioridad: target_macros precomputado > suma de items > opción seleccionada
  const selectedOpt = meal.options?.[selectedOption] || meal.options?.[0];
  const sumItems = (items) => (items || []).reduce((acc, it) => ({
    calories: acc.calories + (Number(it.calories) || 0),
    protein: acc.protein + (Number(it.protein) || 0),
    carbs: acc.carbs + (Number(it.carbs) || 0),
    fats: acc.fats + (Number(it.fats ?? it.fat) || 0),
  }), { calories: 0, protein: 0, carbs: 0, fats: 0 });

  const computed = sumItems(itemsToShow);
  const tm = meal.target_macros || selectedOpt?.macros || {};
  // Leer también los target_* individuales que el backend expone en el meal
  // (target_calories, target_protein, target_carbs, target_fat) y el fallback
  // por opciones/items.
  const baseMealMacros = {
    calories: Math.round(
      tm.calories ?? meal.calories ?? meal.target_calories ?? computed.calories
    ),
    protein: Math.round(
      tm.protein ?? meal.protein ?? meal.target_protein ?? computed.protein
    ),
    carbs: Math.round(
      tm.carbs ?? meal.carbs ?? meal.target_carbs ?? computed.carbs
    ),
    fats: Math.round(
      tm.fats ?? tm.fat ?? meal.fats ?? meal.fat ?? meal.target_fat ?? meal.target_fats ?? computed.fats
    ),
  };
  // Macros que ESTA comida ha recibido como compensación de otras (déficit redistribuido).
  const ci = compIn || { calories: 0, protein: 0, carbs: 0, fats: 0 };
  const hasCompIn = (ci.calories || ci.protein || ci.carbs || ci.fats) > 0.5;
  // El target efectivo de la comida sube si recibe compensación.
  const mealMacros = {
    calories: baseMealMacros.calories + Math.round(ci.calories || 0),
    protein: +(baseMealMacros.protein + (ci.protein || 0)).toFixed(1),
    carbs: +(baseMealMacros.carbs + (ci.carbs || 0)).toFixed(1),
    fats: +(baseMealMacros.fats + (ci.fats || 0)).toFixed(1),
  };
  const hasMealMacros = mealMacros.calories > 0 || mealMacros.protein > 0 || mealMacros.carbs > 0 || mealMacros.fats > 0;

  // Macros ajustadas: restar la porción proporcional de los extras consumidos hoy.
  // Si la usuaria ya marcó esta comida como consumida, NO se ajustan (ya pasó).
  const epm = (isConsumed ? null : extrasPerMeal) || { calories: 0, protein: 0, carbs: 0, fats: 0 };
  const hasExtras = (epm.calories || 0) > 0 || (epm.protein || 0) > 0 || (epm.carbs || 0) > 0 || (epm.fats || 0) > 0;
  const adjustedMacros = {
    calories: Math.max(0, Math.round(mealMacros.calories - (epm.calories || 0))),
    protein: Math.max(0, +(mealMacros.protein - (epm.protein || 0)).toFixed(1)),
    carbs: Math.max(0, +(mealMacros.carbs - (epm.carbs || 0)).toFixed(1)),
    fats: Math.max(0, +(mealMacros.fats - (epm.fats || 0)).toFixed(1)),
  };

  // ===== Suma REAL de macros después de swaps =====
  // IMPORTANTE: NO reescalamos los otros items. Si la cliente cambia harina
  // por cereal, los macros totales reflejan el cambio real (no se "ocultan"
  // moviendo los otros alimentos). Si quedan diferencias, las mostramos abajo.
  const itemKeyFn = (i) => `${activeDietTab}|${mealIndex}|${selectedOption}|${i}`;
  const swapForItem = (i, originalName) => {
    const sw = swapsMap?.[itemKeyFn(i)];
    return (sw && sw.original_food_name === (originalName || '')) ? sw : null;
  };
  let _sumSwappedNew = { calories: 0, protein: 0, carbs: 0, fats: 0 };
  let _sumUnswappedOrig = { calories: 0, protein: 0, carbs: 0, fats: 0 };
  (itemsToShow || []).forEach((it, idx) => {
    const origName = it.food_name || it.name || '';
    const sw = swapForItem(idx, origName);
    if (sw) {
      _sumSwappedNew.calories += Number(sw.macros?.calories) || 0;
      _sumSwappedNew.protein += Number(sw.macros?.protein) || 0;
      _sumSwappedNew.carbs += Number(sw.macros?.carbs) || 0;
      _sumSwappedNew.fats += Number(sw.macros?.fats) || 0;
    } else {
      _sumUnswappedOrig.calories += Number(it.calories) || 0;
      _sumUnswappedOrig.protein += Number(it.protein) || 0;
      _sumUnswappedOrig.carbs += Number(it.carbs) || 0;
      _sumUnswappedOrig.fats += Number(it.fats ?? it.fat) || 0;
    }
  });
  const _hasAnySwap = Object.values(_sumSwappedNew).some(v => v > 0);
  // Sin reescalado automático: el swapScale es 1.0. Los otros items quedan tal cual.
  const _swapScale = 1;
  // Macros REALES de la comida (suma de items, con swaps aplicados, sin reescalado).
  const actualMacros = {
    calories: _sumSwappedNew.calories + _sumUnswappedOrig.calories,
    protein: _sumSwappedNew.protein + _sumUnswappedOrig.protein,
    carbs: _sumSwappedNew.carbs + _sumUnswappedOrig.carbs,
    fats: _sumSwappedNew.fats + _sumUnswappedOrig.fats,
  };
  // Diferencia vs target del plan (mealMacros).
  // Positivo = la comida cambió y te quedó "debiendo" (déficit).
  // Negativo = la comida cambió y tiene MÁS de lo planeado.
  const deficit = {
    calories: +((mealMacros.calories || 0) - actualMacros.calories).toFixed(1),
    protein: +((mealMacros.protein || 0) - actualMacros.protein).toFixed(1),
    carbs: +((mealMacros.carbs || 0) - actualMacros.carbs).toFixed(1),
    fats: +((mealMacros.fats || 0) - actualMacros.fats).toFixed(1),
  };
  // Solo activamos el banner si hay DÉFICIT (>5g) en algún macro principal por un swap.
  const DEF_THRESHOLD = 5;
  const significantDeficits = _hasAnySwap ? [
    deficit.protein > DEF_THRESHOLD ? { key: 'protein', label: 'proteína', value: deficit.protein, unit: 'g' } : null,
    deficit.carbs > DEF_THRESHOLD ? { key: 'carbs', label: 'carbohidratos', value: deficit.carbs, unit: 'g' } : null,
    deficit.fats > DEF_THRESHOLD ? { key: 'fats', label: 'grasas', value: deficit.fats, unit: 'g' } : null,
  ].filter(Boolean) : [];
  const showDeficitBanner = significantDeficits.length > 0 && !isConsumed;

  return (
    <React.Fragment>
      {showPreWorkoutSupps && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-5 backdrop-blur-xl"
          style={{ background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(249, 115, 22, 0.1))', border: '2px solid rgba(239, 68, 68, 0.4)' }}>
          <div className="flex items-center gap-3 mb-3">
            <FaPills className="text-xl" style={{ color: '#EF4444' }} />
            <h4 className="text-lg font-bold uppercase" style={{ color: '#EF4444', fontFamily: 'Poppins, sans-serif' }}>Suplementos Pre-Entreno</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {preWorkoutSupps.map((supp, idx) => (
              <Badge key={idx} className="text-sm px-3 py-1 border-0" style={{ background: 'rgba(239, 68, 68, 0.3)', color: '#FCA5A5' }}>{supp}</Badge>
            ))}
          </div>
        </motion.div>
      )}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + mealIndex * 0.05 }}>
        <div className="rounded-2xl p-6 backdrop-blur-xl transition-opacity"
          style={{
            background: isConsumed
              ? 'rgba(16, 185, 129, 0.06)'
              : (isPostWorkout ? 'linear-gradient(135deg, rgba(249, 115, 22, 0.1), rgba(239, 68, 68, 0.05))' : 'rgba(255, 255, 255, 0.05)'),
            border: isConsumed
              ? '2px solid rgba(16, 185, 129, 0.45)'
              : (isPostWorkout ? '2px solid rgba(249, 115, 22, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)'),
            opacity: isConsumed ? 0.78 : 1,
          }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: isPostWorkout ? 'linear-gradient(135deg, rgba(249, 115, 22, 0.5), rgba(239, 68, 68, 0.5))' : 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.3), rgba(0, 240, 255, 0.3))' }}>
              <span className="text-white font-bold">{mealIndex + 1}</span>
            </div>
            <h4 className="text-xl font-bold text-white uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {isPostWorkout ? 'Comida Post Entreno' : `Comida ${mealIndex + 1}`}
            </h4>
            {isPostWorkout && (
              <Badge className="text-xs font-bold border-0 animate-pulse" style={{ background: 'linear-gradient(135deg, #F97316, #EF4444)', color: 'white' }}>POST ENTRENO</Badge>
            )}
            {(meal.meal_time || meal.time) && <span className="text-gray-500 text-sm ml-auto">{meal.meal_time || meal.time}</span>}
            <button
              onClick={onToggleConsumed}
              data-testid={`toggle-consumed-${mealIndex}`}
              className={`ml-${(meal.meal_time || meal.time) ? '2' : 'auto'} text-xs font-bold px-3 py-1.5 rounded-full border transition-all flex items-center gap-1.5 flex-shrink-0`}
              style={isConsumed
                ? { background: 'rgba(16,185,129,0.18)', borderColor: 'rgba(16,185,129,0.55)', color: '#6EE7B7' }
                : { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.18)', color: '#D1D5DB' }
              }
              title={isConsumed ? 'Marcada como ya consumida — click para desmarcar' : 'Marcar como ya consumida'}
            >
              {isConsumed ? '✓ Ya me la comí' : 'Marcar consumida'}
            </button>
          </div>
          {/* 2026-02 · Biblioteca de recetas (asesoría 1 a 1). Botón secundario
              debajo del header para no colisionar con "consumida". */}
          {canUseRecipeLibrary && !isConsumed && (
            <button
              onClick={() => onOpenRecipeLibrary?.(mealIndex, meal.meal_time || meal.time)}
              className="mb-3 w-full px-3 py-2 rounded-lg text-xs font-semibold border transition-all flex items-center justify-center gap-2"
              style={{ background: 'rgba(236,72,153,0.10)', borderColor: 'rgba(236,72,153,0.45)', color: '#F9A8D4' }}
              data-testid={`open-recipe-library-${mealIndex}`}
            >
              📚 Biblioteca de recetas
              <span className="opacity-70">· agregar opción extra ajustada a tus macros</span>
            </button>
          )}
          {hasMealMacros && (
            <div className="grid grid-cols-4 gap-2 mb-4 p-3 rounded-xl" data-testid={`meal-${mealIndex}-macros`}
              style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
              {[
                { key: 'calories', label: 'Kcal', color: 'var(--brand-primary)', suffix: '' },
                { key: 'protein', label: 'Proteína', color: '#00F0FF', suffix: 'g' },
                { key: 'carbs', label: 'Carbos', color: '#CCFF00', suffix: 'g' },
                { key: 'fats', label: 'Grasas', color: '#FF6B9D', suffix: 'g' },
              ].map((m, i) => {
                const planTarget = mealMacros[m.key];
                const actual = actualMacros[m.key] || 0;
                // Si hay swaps o extras, mostramos el valor REAL de la comida ahora.
                // Si no hay nada cambiado, mostramos el target del plan (igual al actual).
                const showActual = _hasAnySwap || hasExtras;
                const display = showActual
                  ? (m.key === 'calories' ? Math.round(actual) : +actual.toFixed(1))
                  : (m.key === 'calories' ? Math.round(planTarget) : +planTarget.toFixed(1));
                const diff = +(actual - planTarget).toFixed(1);
                const showDiff = showActual && Math.abs(diff) > (m.key === 'calories' ? 5 : 1);
                return (
                  <div key={i} className="flex flex-col items-center justify-center text-center">
                    <p className="text-lg md:text-xl font-extrabold leading-tight" style={{ color: m.color, fontFamily: 'Poppins, sans-serif' }}>
                      {display}{m.suffix}
                    </p>
                    {showActual && (
                      <p className="text-[10px] text-gray-500 leading-tight">
                        plan {m.key === 'calories' ? Math.round(planTarget) : +planTarget.toFixed(1)}{m.suffix}
                      </p>
                    )}
                    {showDiff && (
                      <p className="text-[10px] leading-tight font-semibold"
                         style={{ color: diff > 0 ? '#F87171' : '#34D399' }}>
                        {diff > 0 ? '+' : ''}{diff}{m.suffix}
                      </p>
                    )}
                    <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wider mt-0.5">{m.label}</p>
                  </div>
                );
              })}
              {hasExtras && (
                <div className="col-span-4 mt-2 pt-2 border-t border-white/10">
                  <p className="text-[10px] text-amber-300 text-center">
                    ⚖️ Ajustado por las comidas extra que registraste hoy.
                  </p>
                </div>
              )}
              {hasCompIn && (
                <div className="col-span-4 mt-2 pt-2 border-t border-white/10">
                  <p className="text-[10px] text-cyan-300 text-center">
                    ✨ Esta comida recibió un refuerzo automático
                    {ci.protein > 0.5 ? ` +${(+ci.protein).toFixed(1)}g proteína` : ''}
                    {ci.carbs > 0.5 ? ` +${(+ci.carbs).toFixed(1)}g carbos` : ''}
                    {ci.fats > 0.5 ? ` +${(+ci.fats).toFixed(1)}g grasas` : ''}
                    {' '}para compensar otra comida que cambiaste.
                  </p>
                </div>
              )}
            </div>
          )}
          {showDeficitBanner && (
            <div className="mb-4 p-3 rounded-xl flex items-start gap-3" data-testid={`deficit-banner-${mealIndex}`}
              style={{ background: 'rgba(251, 191, 36, 0.10)', border: '1px solid rgba(251, 191, 36, 0.45)' }}>
              <span className="text-xl leading-none">⚠️</span>
              <div className="flex-1">
                <p className="text-amber-200 text-sm font-semibold leading-snug">
                  Te faltan {significantDeficits.map((d, i) => (
                    <span key={d.key}>
                      <strong>{d.value}{d.unit} de {d.label}</strong>
                      {i < significantDeficits.length - 1 ? ', ' : ''}
                    </span>
                  ))} por este cambio.
                </p>
                <p className="text-amber-300/80 text-xs mt-0.5">
                  Si quieres, el sistema lo agrega automáticamente a tus otras comidas pendientes del día.
                </p>
                <div className="flex gap-2 mt-2">
                  {hasActiveDistribution ? (
                    <button
                      onClick={() => onUndoDistribution?.(activeDietTab, mealIndex)}
                      data-testid={`undo-distribute-${mealIndex}`}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                      style={{ background: 'rgba(255,255,255,0.10)', color: '#FCD34D', border: '1px solid rgba(252, 211, 77, 0.35)' }}
                    >
                      Deshacer reparto
                    </button>
                  ) : (
                    <button
                      onClick={() => onDistributeDeficit?.(activeDietTab, mealIndex, deficit)}
                      data-testid={`distribute-deficit-${mealIndex}`}
                      className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                      style={{ background: 'linear-gradient(135deg, #F59E0B, #F97316)', color: '#1F2937' }}
                    >
                      Agregar en otra comida
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          {hasMultipleOptions && numOptions > 1 && (
            <div className="mb-4">
              <p className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Opciones disponibles:</p>
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: numOptions }).map((_, optIdx) => (
                  <button key={optIdx} onClick={() => setSelectedOptions(prev => ({ ...prev, [mealIndex]: optIdx }))}
                    className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${selectedOption === optIdx ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                    style={{
                      background: selectedOption === optIdx ? 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' : 'rgba(255, 255, 255, 0.1)',
                      border: selectedOption === optIdx ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'
                    }}>Opcion {optIdx + 1}</button>
                ))}
              </div>
            </div>
          )}
          {itemsToShow.length === 0 ? (
            <p className="text-gray-500 text-sm italic">Alimentos pendientes de agregar...</p>
          ) : (
            <div className="space-y-3">
              {(() => {
                // ===== PASADA 1: detectar swaps válidos y calcular sumas =====
                // Swap "válido" = existe en swapsMap Y conserva el original_food_name del item
                // (si el admin reguardó el plan con otro alimento, el swap viejo se ignora).
                const itemKey = (i) => `${activeDietTab}|${mealIndex}|${selectedOption}|${i}`;
                const swapForItem = (i, originalName) => {
                  const sw = swapsMap?.[itemKey(i)];
                  return (sw && sw.original_food_name === (originalName || '')) ? sw : null;
                };

                // Suma de las macros NUEVAS (post-swap) y de las ORIGINALES de los items NO cambiados
                let sumSwappedNew = { calories: 0, protein: 0, carbs: 0, fats: 0 };
                let sumUnswappedOrig = { calories: 0, protein: 0, carbs: 0, fats: 0 };
                itemsToShow.forEach((it, idx) => {
                  const origName = it.food_name || it.name || '';
                  const sw = swapForItem(idx, origName);
                  if (sw) {
                    sumSwappedNew.calories += Number(sw.macros?.calories) || 0;
                    sumSwappedNew.protein += Number(sw.macros?.protein) || 0;
                    sumSwappedNew.carbs += Number(sw.macros?.carbs) || 0;
                    sumSwappedNew.fats += Number(sw.macros?.fats) || 0;
                  } else {
                    sumUnswappedOrig.calories += Number(it.calories) || 0;
                    sumUnswappedOrig.protein += Number(it.protein) || 0;
                    sumUnswappedOrig.carbs += Number(it.carbs) || 0;
                    sumUnswappedOrig.fats += Number(it.fats ?? it.fat) || 0;
                  }
                });

                // Factor de escala adicional por swaps:
                // YA NO REESCALAMOS los items no cambiados. Si la cliente cambia
                // un alimento, los OTROS quedan tal cual y la diferencia de macros
                // se muestra en el cuadro de la comida (real vs plan).
                const hasAnySwap = Object.values(sumSwappedNew).some(v => v > 0);
                const swapScale = 1;

                // Factor de escala por extras (idéntico al anterior).
                const scaleFactor = (hasExtras && !isConsumed && mealMacros.calories > 0)
                  ? Math.max(0, adjustedMacros.calories / mealMacros.calories)
                  : 1;
                const scaled = scaleFactor !== 1;

                return itemsToShow.map((item, itemIndex) => {
                  const itemOriginalName = item.food_name || item.name || '';
                  const swap = swapForItem(itemIndex, itemOriginalName);
                  // Si hay swap, usar los datos del swap como "alimento actual"
                  const effectiveItem = swap ? {
                    food_name: swap.new_food_name,
                    quantity: `${swap.grams}${swap.unit || 'g'}`,
                    calories: swap.macros?.calories,
                    protein: swap.macros?.protein,
                    carbs: swap.macros?.carbs,
                    fats: swap.macros?.fats,
                    category: item.category,
                  } : item;
                  const origQty = effectiveItem.quantity || (effectiveItem.grams != null ? `${effectiveItem.grams}` : '');
                  const origCal = Number(effectiveItem.calories) || 0;
                  // Factor de escala para este item específico:
                  //  - Items cambiados (swap): NO se escalan por swapScale (su gramaje ya está calculado exacto).
                  //  - Items NO cambiados: SÍ se escalan para compensar las kcal que el swap movió.
                  //  - Encima, todos se escalan por extras (extras_per_meal del tracker diario).
                  const localSwapScale = swap ? 1 : swapScale;
                  const itemScale = (scaled ? scaleFactor : 1) * localSwapScale;
                  const itemScaled = itemScale !== 1;
                  // Intentar escalar la cantidad si tiene un número al inicio
                  let displayQty = '';
                  let origQtyDisplay = '';
                  if (origQty) {
                    const qtyStr = String(origQty);
                    const match = qtyStr.match(/^(\d+(?:[.,]\d+)?)\s*(.*)$/);
                    if (match) {
                      const num = parseFloat(match[1].replace(',', '.'));
                      const suffix = (match[2] || '').trim();
                      const finalSuffix = suffix || 'g';
                      origQtyDisplay = `${Math.round(num)}${finalSuffix.startsWith('g') || finalSuffix.startsWith('ml') ? finalSuffix : ' ' + finalSuffix}`;
                      const newNum = itemScaled ? num * itemScale : num;
                      const rounded = newNum < 10 ? +newNum.toFixed(1) : Math.round(newNum);
                      displayQty = `${rounded}${finalSuffix.startsWith('g') || finalSuffix.startsWith('ml') ? finalSuffix : ' ' + finalSuffix}`;
                    } else {
                      displayQty = qtyStr.includes('g') || qtyStr.includes('ml') ? qtyStr : `${qtyStr}g`;
                      origQtyDisplay = displayQty;
                    }
                  }
                  const displayCal = itemScaled ? Math.round(origCal * itemScale) : Math.round(origCal);
                  const currentDisplayMacros = {
                    calories: displayCal,
                    protein: +(Number(effectiveItem.protein) || 0) * itemScale,
                    carbs: +(Number(effectiveItem.carbs) || 0) * itemScale,
                    fats: +(Number(effectiveItem.fats ?? effectiveItem.fat) || 0) * itemScale,
                  };
                  const canSwap = !isConsumed && !!onSwapItem;
                  return (
                    <div
                      key={itemIndex}
                      className={`flex items-center justify-between p-3 rounded-xl ${canSwap ? 'cursor-pointer hover:bg-white/10 transition-colors' : ''}`}
                      style={{ background: swap ? 'rgba(var(--brand-primary-glow), 0.08)' : 'rgba(255, 255, 255, 0.03)', border: swap ? '1px solid rgba(var(--brand-primary-glow), 0.30)' : '1px solid transparent' }}
                      onClick={canSwap ? () => onSwapItem(itemIndex, effectiveItem, currentDisplayMacros, mealMacros, itemsToShow, selectedOption) : undefined}
                      title={canSwap ? 'Click para cambiar este alimento por otro equivalente' : ''}
                      data-testid={`meal-${mealIndex}-item-${itemIndex}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getFoodEmoji(effectiveItem.food_name || effectiveItem.name, effectiveItem.category)}</span>
                        <div>
                          <p className="text-white font-medium flex items-center gap-2">
                            {effectiveItem.food_name || effectiveItem.name}
                            {swap && <span className="text-[9px] uppercase text-pink-300 bg-pink-500/15 px-1.5 py-0.5 rounded">Cambiado</span>}
                            {!swap && hasAnySwap && itemScaled && swapScale !== 1 && (
                              <span className="text-[9px] uppercase text-cyan-300 bg-cyan-500/15 px-1.5 py-0.5 rounded">Ajustado</span>
                            )}
                          </p>
                          <p className="text-gray-500 text-xs">
                            {displayQty}
                            {itemScaled && origQtyDisplay && origQtyDisplay !== displayQty && (
                              <span className="text-gray-600 line-through ml-1.5">{origQtyDisplay}</span>
                            )}
                            {canSwap && <span className="text-pink-400 ml-2 text-[10px]">⇄ tocar para cambiar</span>}
                          </p>
                        </div>
                      </div>
                      {origCal > 0 && (
                        <span className="text-sm flex items-baseline gap-1.5" style={{ color: 'var(--brand-primary)' }}>
                          {displayCal} kcal
                          {itemScaled && origCal !== displayCal && (
                            <span className="text-gray-600 line-through text-[11px]">{Math.round(origCal)}</span>
                          )}
                        </span>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
          {meal.supplements && meal.supplements.length > 0 && meal.supplements.some(s => s && s.trim()) && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <FaPills className="text-purple-400" />
                <span className="text-sm font-semibold text-purple-300">Suplementos</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {meal.supplements.filter(s => s && s.trim()).map((supp, suppIndex) => (
                  <Badge key={suppIndex} className="px-3 py-1 text-sm"
                    style={{ backgroundColor: 'rgba(168, 85, 247, 0.2)', color: '#c4b5fd', border: '1px solid rgba(168, 85, 247, 0.4)' }}>
                    {supp}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </React.Fragment>
  );
};

// ========== MAIN COMPONENT ==========

const MyDiet = () => {
  const navigate = useNavigate();
  const [mealPlan, setMealPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formStatus, setFormStatus] = useState(null);
  const [multiDiets, setMultiDiets] = useState(null);
  const [activeDietTab, setActiveDietTab] = useState('training_inferior');
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showNotes, setShowNotes] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingClinical, setDownloadingClinical] = useState(false);
  // Main view tab: 'diet' or 'clinical'
  const [mainTab, setMainTab] = useState('diet');
  // Macros consumidos en extras hoy → para ajustar las comidas planeadas proporcionalmente
  const [extrasTotals, setExtrasTotals] = useState({ calories: 0, protein: 0, carbs: 0, fats: 0 });
  // Comidas planeadas marcadas como ya consumidas (Set de "tab|index")
  const [consumedKeys, setConsumedKeys] = useState(new Set());
  // Swaps de alimentos: clave "tab|mealIdx|itemIdx" → { new_food_name, grams, unit, macros }
  const [swapsMap, setSwapsMap] = useState({});
  // Compensaciones (déficit repartido entre comidas). Map por target_meal_index dentro del tab activo.
  // Cada valor es la SUMA de macros añadidos a esa comida desde otras.
  const [compensations, setCompensations] = useState([]);
  // Estado del diálogo de swap
  const [swapDialog, setSwapDialog] = useState(null); // { food, foodCategory, currentMacros, mealCapacity, dietTab, mealIndex, itemIndex }
  // 2026-02 · Biblioteca de recetas (asesoría 1 a 1)
  const [recipeLibrary, setRecipeLibrary] = useState(null); // { mealIndex, mealTime } | null

  useEffect(() => { fetchData(); }, []);

  // Cargar comidas marcadas como consumidas hoy
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/macro-log/consumed-meals`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('deisycfit_token') || ''}` },
        });
        const items = res.data?.items || [];
        setConsumedKeys(new Set(items.map(it => `${it.diet_tab}|${it.meal_index}`)));
      } catch (e) {
        // silencioso
      }
    })();
  }, []);

  // Cargar swaps de alimentos hoy
  const loadSwaps = async () => {
    try {
      const res = await axios.get(`${API}/macro-log/swaps`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('deisycfit_token') || ''}` },
      });
      const items = res.data?.items || [];
      const map = {};
      items.forEach(s => {
        // Key incluye option_index (default 0 para swaps antiguos sin esta propiedad).
        const opt = (s.option_index ?? 0);
        map[`${s.diet_tab}|${s.meal_index}|${opt}|${s.item_index}`] = s;
      });
      setSwapsMap(map);
    } catch (e) {
      // silencioso
    }
  };
  useEffect(() => { loadSwaps(); }, []);

  // Cargar compensaciones del día
  const loadCompensations = async () => {
    try {
      const res = await axios.get(`${API}/macro-log/compensations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('deisycfit_token') || ''}` },
      });
      setCompensations(res.data?.items || []);
    } catch (e) {
      // silencioso
    }
  };
  useEffect(() => { loadCompensations(); }, []);

  const toggleMealConsumed = async (dietTab, mealIndex) => {
    const key = `${dietTab}|${mealIndex}`;
    const wasConsumed = consumedKeys.has(key);
    // Optimistic update
    const next = new Set(consumedKeys);
    if (wasConsumed) next.delete(key); else next.add(key);
    setConsumedKeys(next);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('deisycfit_token') || ''}` };
      if (wasConsumed) {
        await axios.delete(`${API}/macro-log/consumed-meals`, {
          params: { diet_tab: dietTab, meal_index: mealIndex }, headers,
        });
      } else {
        await axios.post(`${API}/macro-log/consumed-meals`, { diet_tab: dietTab, meal_index: mealIndex }, { headers });
      }
    } catch (e) {
      // revertir si falla
      setConsumedKeys(consumedKeys);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('deisycfit_token');
      const savedUser = localStorage.getItem('deisycfit_user');
      let userEmail = null;
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        userEmail = userData.email;
      }
      
      const processPlan = (data) => {
        if (data && (data.meals || data.diet_data || data.id)) {
          setMealPlan(data);
          if (data.multi_diets) {
            setMultiDiets(data.multi_diets);
            const tabs = ['training_inferior', 'training_superior', 'descanso', 'lineal'];
            for (const tab of tabs) {
              if (data.multi_diets[tab]?.meals?.length > 0) { setActiveDietTab(tab); break; }
            }
          } else if (data.day_type) {
            setActiveDietTab(data.day_type);
          }
          return true;
        }
        return false;
      };
      
      try {
        const response = await axios.get(`${API}/meal-plan/active/current`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (processPlan(response.data)) return;
        else if (response.data?.pending_release) {
          setFormStatus({ status: 'pending', form_completed: true, message: response.data.message });
        } else if (userEmail) {
          const statusResponse = await axios.get(`${API}/personalized-plans/check-status/${encodeURIComponent(userEmail)}`);
          setFormStatus(statusResponse.data);
        }
      } catch (error) {
        if (error.response?.status === 401 && userEmail) {
          try {
            const response = await axios.get(`${API}/meal-plan/active/by-email/${encodeURIComponent(userEmail)}`);
            if (processPlan(response.data)) return;
            else if (response.data?.pending_release) { setFormStatus({ status: 'pending', form_completed: true, message: response.data.message }); return; }
          } catch (e) { console.error('Error with fallback endpoint:', e); }
        }
        if (userEmail) {
          try {
            const statusResponse = await axios.get(`${API}/personalized-plans/check-status/${encodeURIComponent(userEmail)}`);
            setFormStatus(statusResponse.data);
          } catch (e) { console.error('Error checking form status:', e); }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally { setLoading(false); }
  };

  const handleDownloadPDF = async () => {
    if (downloading) { toast.info('Ya hay una descarga en progreso...'); return; }
    const planId = mealPlan?.id;
    if (!planId) { toast.error('No se puede descargar: plan sin identificador'); return; }
    try {
      setDownloading(true);
      toast.info('Generando PDF de dieta...');
      const token = localStorage.getItem('deisycfit_token');
      let downloadUrl = `${API}/download/diet-pdf/${planId}`;
      const currentTab = activeDietTab;
      if (multiDiets && multiDiets[currentTab]) {
        downloadUrl = `${API}/download/diet-pdf/${planId}?day_type=${currentTab}`;
      }
      const response = await axios.get(downloadUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        responseType: 'blob', timeout: 60000
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dayLabels = { 'training_inferior': 'Dia_Inferior', 'training_superior': 'Dia_Superior', 'descanso': 'Dia_Descanso', 'lineal': 'Dieta_Lineal' };
      // Usar el nombre individual de la dieta activa si existe, sino el nombre del plan
      const activeDietName = (multiDiets && multiDiets[currentTab]?.diet_name) || mealPlan.name || 'plan_alimentacion';
      let cleanName = activeDietName;
      const dayPatterns = ['dia de inferior', 'día de inferior', 'dia inferior', 'día inferior', 'dia de superior', 'día de superior', 'dia superior', 'día superior', 'dia de descanso', 'día de descanso', 'dieta lineal', 'training_inferior', 'training_superior'];
      dayPatterns.forEach(p => { cleanName = cleanName.replace(new RegExp(p, 'gi'), '').trim(); });
      cleanName = cleanName.replace(/^[\s\-–/]+|[\s\-–/]+$/g, '').trim() || 'plan_alimentacion';
      const ts = Date.now();
      const fileName = multiDiets ? `${dayLabels[currentTab] || 'Dieta'}_${cleanName.replace(/\s+/g, '_')}_${ts}.pdf` : `Dieta_${cleanName.replace(/\s+/g, '_')}_${ts}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF de dieta descargado!');
    } catch (error) {
      console.error('Error downloading diet PDF:', error);
      if (error.response?.status === 404) toast.error('Plan no encontrado. Contacta a soporte.');
      else if (error.code === 'ECONNABORTED') toast.error('La descarga tardo demasiado. Intenta de nuevo.');
      else toast.error('Error al descargar el PDF. Intenta de nuevo.');
    } finally { setDownloading(false); }
  };

  const handleDownloadClinicalPDF = async () => {
    if (downloadingClinical) { toast.info('Ya hay una descarga en progreso...'); return; }
    const planId = mealPlan?.id;
    if (!planId) { toast.error('No se puede descargar: plan sin identificador'); return; }
    try {
      setDownloadingClinical(true);
      toast.info('Generando PDF de ajustes clinicos...');
      const token = localStorage.getItem('deisycfit_token');
      const response = await axios.get(`${API}/download/clinical-pdf/${planId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        responseType: 'blob', timeout: 60000
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Ajustes_Clinicos_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF de ajustes clinicos descargado!');
    } catch (error) {
      console.error('Error downloading clinical PDF:', error);
      if (error.response?.status === 404) toast.error('No hay datos clinicos disponibles.');
      else toast.error('Error al descargar. Intenta de nuevo.');
    } finally { setDownloadingClinical(false); }
  };

  // ========== LOADING / EMPTY STATES ==========

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 animate-pulse" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }} />
          <p className="text-white text-lg">Cargando tu plan...</p>
        </div>
      </div>
    );
  }

  if (!mealPlan && formStatus?.form_completed === true && (formStatus?.status === 'pending' || formStatus?.status === 'preparing' || formStatus?.status === 'sent')) {
    // Fit Evolution: mostrar countdown / mensaje de preparación
    const isFitEvolutionPreparing = formStatus?.status === 'preparing';
    const isFitEvolutionReady = formStatus?.status === 'sent';
    return (
      <div className="min-h-screen py-12 px-6 relative" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 30% 70%, rgba(var(--brand-primary-glow), 0.15), transparent 50%)' }} />
        <div className="relative z-10 max-w-4xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6 text-gray-400 hover:text-white"><FaArrowLeft className="mr-2" /> Volver</Button>
          <div className="rounded-2xl p-12 text-center backdrop-blur-xl" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #FF69B4)', boxShadow: '0 0 40px rgba(var(--brand-primary-glow), 0.4)' }}>
              <FaClock className="text-4xl text-white animate-pulse" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-white uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {isFitEvolutionReady ? (<>Tu Plan Fit<br /><span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}>Evolution</span></>) : (<>Tu Plan Esta en<br /><span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}>Preparacion</span></>)}
            </h1>
            <p className="text-gray-400 mb-8 text-lg max-w-md mx-auto">
              {isFitEvolutionReady
                ? 'Tu plan de nutrición está siendo cargado por Deisy. Ya recibirás la notificación cuando esté visible aquí.'
                : (isFitEvolutionPreparing
                    ? 'Tu plan Fit Evolution estará listo en máximo 24 horas. Te notificaremos cuando esté disponible.'
                    : 'Deisy esta preparando tu plan nutricional personalizado. Te notificaremos por email cuando este listo.')}
            </p>
            {isFitEvolutionPreparing && (
              <Button
                onClick={() => navigate('/fit-evolution/preparing')}
                className="rounded-full px-8 py-6 font-bold uppercase tracking-wider text-white"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                data-testid="fe-diet-view-countdown-btn"
              >
                Ver Countdown 24h
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!mealPlan) {
    return (
      <div className="min-h-screen py-12 px-6 relative" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 30% 70%, rgba(var(--brand-primary-glow), 0.15), transparent 50%), radial-gradient(circle at 70% 30%, rgba(0, 240, 255, 0.1), transparent 40%)' }} />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-8 text-gray-400 hover:text-white absolute top-0 left-0"><FaArrowLeft className="mr-2" /> Volver</Button>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="pt-20">
            <div className="inline-flex items-center justify-center w-28 h-28 rounded-full mb-8" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)', boxShadow: '0 0 60px rgba(var(--brand-primary-glow), 0.4)' }}>
              <FaAppleAlt className="text-5xl text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white uppercase tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Mi Plan de<br /><span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}>Alimentacion</span>
            </h1>
            <p className="text-gray-400 mb-8 text-lg max-w-xl mx-auto">Completa el formulario para que Deisy pueda crear tu plan nutricional personalizado</p>
            <Button onClick={() => navigate('/personalized-form')} className="rounded-full px-8 py-6 font-bold uppercase tracking-wider text-white"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)', boxShadow: '0 0 30px rgba(var(--brand-primary-glow), 0.4)' }}>
              Llenar Formulario
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (mealPlan.pending_release || mealPlan.is_released === false) {
    return (
      <div className="min-h-screen py-12 px-6 relative" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 30% 70%, rgba(var(--brand-primary-glow), 0.15), transparent 50%)' }} />
        <div className="relative z-10 max-w-4xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6 text-gray-400 hover:text-white"><FaArrowLeft className="mr-2" /> Volver</Button>
          <div className="rounded-2xl p-12 text-center backdrop-blur-xl" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="inline-flex items-center justify-center w-28 h-28 rounded-full mb-8" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)', boxShadow: '0 0 40px rgba(var(--brand-primary-glow), 0.4)' }}>
              <FaClock className="text-5xl text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-6 text-white uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>Estamos preparando tu dieta!</h1>
            <p className="text-xl text-gray-300 mb-4">{mealPlan.message || "Tu plan de alimentacion esta siendo preparado por Deisy."}</p>
            <p className="text-2xl font-bold" style={{ color: '#00F0FF' }}>En un plazo maximo de 48 horas estara listo</p>
          </div>
        </div>
      </div>
    );
  }

  // ========== MAIN PLAN VIEW ==========

  const hasUnlimitedAccess = mealPlan.is_personalized_plan || mealPlan.unlimited_access;
  // 2026-02 · Asesoría 1 a 1: biblioteca de recetas disponible sólo para estos planes.
  // Normalización defensiva: acepta mayúsculas, espacios, guiones bajos, prefijos
  // "renewal_/renewal-/plan_/plan-/vip-renewal-" y alias "gold_vip"→"vip_gold".
  const _normalizePlan = (raw) => {
    if (!raw) return '';
    let s = String(raw).trim().toLowerCase();
    const prefixes = ['vip-renewal-', 'vip_renewal_', 'renewal-', 'renewal_', 'plan-', 'plan_', 'plan '];
    for (const p of prefixes) { if (s.startsWith(p)) { s = s.slice(p.length); break; } }
    const aliases = {
      'basico': 'basic', 'básico': 'basic',
      'premium_estetico': 'premium', 'premium estetico': 'premium',
      'gold_vip': 'vip_gold', 'vip-gold': 'vip_gold', 'goldvip': 'vip_gold',
      'competitiva': 'competitivo',
    };
    return aliases[s] || s;
  };
  // Misma regla que backend `_user_is_asesoria`: tolerante a idioma, espacios y acentos
  // ('competitive', 'gold vip', 'plan_vip', 'Programa de Transformación', ...).
  // 2026-02 · Fit Evolution (basic) también accede a la biblioteca — decisión
  // del cliente. Se incluye la keyword `fit_evolution` y el valor canónico `basic`.
  const _ASESORIA_KEYWORDS = ['premium', 'gold', 'vip', 'competitiv', 'competencia', 'transformacion', 'asesoria', '1a1', '1_a_1', 'fit_evolution', 'fitevolution'];
  const _isAsesoriaPlan = (raw) => {
    if (!raw) return false;
    if (['basic', 'premium', 'gold', 'vip_gold', 'competitivo'].includes(_normalizePlan(raw))) return true;
    const key = String(raw).normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .trim().toLowerCase().replace(/[\s-]+/g, '_').replace(/^_+|_+$/g, '');
    return _ASESORIA_KEYWORDS.some((k) => key.includes(k));
  };
  const canUseRecipeLibrary = mealPlan.can_use_recipe_library === true  // decisión del backend (misma regla que la biblioteca)
    || mealPlan.is_personalized_plan === true
    || mealPlan.program_type === 'asesoria_1a1'
    || [mealPlan.plan_type, mealPlan.subscription_plan].some(_isAsesoriaPlan);
  
  // Macros: usar datos del tab activo de multi_diets si existen
  const activeDiet = multiDiets?.[activeDietTab];
  const macros = mealPlan.macros || {
    calories: activeDiet?.target_calories || mealPlan.total_calories || 0,
    protein: activeDiet?.target_protein || mealPlan.total_protein || 0,
    carbs: activeDiet?.target_carbs || mealPlan.total_carbs || 0,
    fats: activeDiet?.target_fat || activeDiet?.target_fats || mealPlan.total_fat || 0
  };

  // Extract clinical data
  const clinicalSummary = mealPlan?.user_clinical_summary || {};
  const clinicalData = mealPlan?.clinical_data || {};
  const hasClinicalData = !!(
    (clinicalSummary.conditions_identified?.length > 0) ||
    (clinicalData.conditions_applied?.length > 0)
  );
  const recommendedSupplements = clinicalSummary.recommended_supplements || clinicalData.recommended_supplements || [];
  const avoidSupplements = clinicalSummary.avoid_supplements || clinicalData.avoid_supplements || [];
  const avoidFoods = clinicalSummary.avoid_foods || clinicalData.avoid_foods_display || [];
  const conditions = clinicalSummary.conditions_identified || (clinicalData.conditions_applied || []).map(c => c.label || c);
  const recommendations = clinicalSummary.recommendations || (clinicalData.active_notes || []).map(n => n.body || n);
  const actionPlans = clinicalSummary.action_plans || (clinicalData.active_actions || []).map(a => ({ title: a.title, clinical_objective: a.clinical_objective || '', actions: a.actions, monitoring_points: a.monitoring_points || [], follow_up: a.follow_up || '', duration: a.duration }));
  const substitutions = clinicalSummary.substitutions || Object.entries(clinicalData.substitution_map || {}).slice(0, 12).map(([k, v]) => ({ original: k, replacements: Array.isArray(v) ? v.slice(0, 3) : [v] }));

  return (
    <div className="min-h-screen py-8 px-4 md:px-6 relative" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="fixed inset-0 z-0 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.1), transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 40%)' }} />
      
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white" data-testid="back-button">
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
        </div>

        {/* Title & Badges */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] mb-3" style={{ color: 'var(--brand-primary)' }}>Tu plan de alimentacion</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 uppercase tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }} data-testid="diet-title">
            {(activeDiet && activeDiet.diet_name) || mealPlan.name || 'Mi Dieta'}
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="px-4 py-2 rounded-full text-sm font-bold uppercase" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)', color: 'white' }}>
              Mes {mealPlan.month_number || 1}
            </span>
            {hasUnlimitedAccess && (
              <span className="px-4 py-2 rounded-full text-sm font-bold uppercase" style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF', border: '1px solid rgba(0, 240, 255, 0.3)' }}>
                Ilimitado
              </span>
            )}
          </div>
        </motion.div>

        {/* ===== MAIN TABS: Plan Nutricional | Ajustes Clinicos ===== */}
        {hasClinicalData && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <div className="flex gap-2 p-1 rounded-2xl" style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <button onClick={() => setMainTab('diet')} data-testid="main-tab-diet"
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm uppercase tracking-wider transition-all ${mainTab === 'diet' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
                style={{ background: mainTab === 'diet' ? 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.3), rgba(0, 240, 255, 0.2))' : 'transparent', border: mainTab === 'diet' ? '1px solid rgba(var(--brand-primary-glow), 0.4)' : '1px solid transparent' }}>
                <FaUtensils /> Plan Nutricional
              </button>
              <button onClick={() => setMainTab('clinical')} data-testid="main-tab-clinical"
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm uppercase tracking-wider transition-all ${mainTab === 'clinical' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
                style={{ background: mainTab === 'clinical' ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(6, 95, 70, 0.2))' : 'transparent', border: mainTab === 'clinical' ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid transparent' }}>
                <FaHeartbeat /> Ajustes Clinicos
              </button>
            </div>
          </motion.div>
        )}

        {/* ===== DIET TAB CONTENT ===== */}
        {mainTab === 'diet' && (
          <>
            {/* Download Diet PDF */}
            <div className="flex justify-end mb-6">
              <Button onClick={handleDownloadPDF} disabled={downloading} data-testid="download-diet-pdf-btn"
                className="rounded-full px-6 font-bold uppercase tracking-wider text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}>
                {downloading ? <><span className="animate-spin mr-2">...</span> Generando...</> : <><FaDownload className="mr-2" /> Descargar Dieta PDF</>}
              </Button>
            </div>

            {/* Macros */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-8">
              <div className="rounded-2xl p-6 backdrop-blur-xl" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <h3 className="text-lg font-bold text-white mb-4 uppercase flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <FaFire style={{ color: 'var(--brand-primary)' }} /> Macronutrientes Diarios
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Calorias', value: macros.calories, color: 'var(--brand-primary)' },
                    { label: 'Proteina', value: `${macros.protein}g`, color: '#00F0FF' },
                    { label: 'Carbos', value: `${macros.carbs}g`, color: '#CCFF00' },
                    { label: 'Grasas', value: `${macros.fats}g`, color: '#FF6B9D' }
                  ].map((macro, idx) => (
                    <div key={idx} className="text-center">
                      <p className="text-3xl font-bold" style={{ color: macro.color }}>{macro.value}</p>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">{macro.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* ===== Composición Corporal y Proyecciones (2026-02) ===== */}
            {(() => {
              const bc = mealPlan.body_composition;
              const daysToTarget = (bc && bc.weeks_to_target != null) ? bc.weeks_to_target * 7 : null;
              // Fecha estimada: usa target_date_label del payload si viene; si no, calcula ahora + días
              let dateLabel = bc && bc.target_date_label ? bc.target_date_label : null;
              if (!dateLabel && daysToTarget != null && daysToTarget > 0) {
                const d = new Date();
                d.setDate(d.getDate() + daysToTarget);
                dateLabel = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
              }
              const hasData = bc && (
                bc.body_fat_pct != null ||
                bc.body_fat_pct_target != null ||
                (bc.weekly_fat_loss_kg && bc.weekly_fat_loss_kg > 0) ||
                daysToTarget != null
              );
              if (!hasData) return null;
              const cells = [
                bc.body_fat_pct != null && { label: '% Grasa Actual', value: `${bc.body_fat_pct}%`, color: '#34D399' },
                bc.body_fat_pct_target != null && { label: '% Grasa Objetivo', value: `${bc.body_fat_pct_target}%`, color: '#00F0FF' },
                bc.weekly_fat_loss_kg > 0 && { label: 'Pérdida Grasa/Semana', value: `${bc.weekly_fat_loss_kg} kg`, color: '#FF6B9D' },
                daysToTarget != null && { label: 'Días al Objetivo', value: daysToTarget, subtitle: dateLabel ? `hasta ${dateLabel}` : null, color: '#FBBF24' }
              ].filter(Boolean);
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="mb-8"
                  data-testid="body-composition-section"
                >
                  <div className="rounded-2xl p-6 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <h3 className="text-lg font-bold text-white mb-4 uppercase flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      📊 Composición Corporal y Proyecciones
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {cells.map((c, i) => (
                        <div key={i} className="text-center" data-testid={`body-comp-${i}`}>
                          <p className="text-2xl md:text-3xl font-bold" style={{ color: c.color }}>{c.value}</p>
                          <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">{c.label}</p>
                          {c.subtitle && (
                            <p className="text-[11px] text-gray-400 mt-0.5" data-testid={`body-comp-${i}-subtitle`}>{c.subtitle}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })()}

            {/* ===== Tracker diario: macros disponibles + ingreso de comidas extra ===== */}
            <DailyMacroTracker target={macros} onTotalsChange={setExtrasTotals} />

            {/* Diet day type tabs */}
            {multiDiets && Object.values(multiDiets).some(d => d?.meals?.length > 0) && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                <div className="flex flex-wrap gap-2 mb-6">
                  {['training_inferior', 'training_superior', 'descanso', 'lineal'].map((tabKey) => {
                    const diet = multiDiets[tabKey];
                    if (!diet?.meals?.length) return null;
                    const tabInfo = DIET_TAB_LABELS[tabKey];
                    const IconComponent = tabInfo.icon;
                    const isActive = activeDietTab === tabKey && !showNotes;
                    return (
                      <button key={tabKey} onClick={() => { setActiveDietTab(tabKey); setShowNotes(false); }}
                        className={`flex items-center gap-2 px-5 py-3 rounded-full font-bold uppercase tracking-wider transition-all ${isActive ? 'text-white scale-105' : 'text-gray-400 hover:text-white'}`}
                        style={{
                          background: isActive ? `linear-gradient(135deg, ${tabInfo.color}, ${tabInfo.color}88)` : 'rgba(255, 255, 255, 0.05)',
                          border: `2px solid ${isActive ? tabInfo.color : 'rgba(255, 255, 255, 0.1)'}`,
                          boxShadow: isActive ? `0 0 20px ${tabInfo.color}40` : 'none'
                        }}>
                        <IconComponent /> {tabInfo.name}
                      </button>
                    );
                  })}
                  {mealPlan?.custom_pages?.length > 0 && (
                    <button onClick={() => setShowNotes(true)}
                      className={`flex items-center gap-2 px-5 py-3 rounded-full font-bold uppercase tracking-wider transition-all ${showNotes ? 'text-white scale-105' : 'text-gray-400 hover:text-white'}`}
                      style={{
                        background: showNotes ? 'linear-gradient(135deg, #F59E0B, #D97706)' : 'rgba(255, 255, 255, 0.05)',
                        border: `2px solid ${showNotes ? '#F59E0B' : 'rgba(255, 255, 255, 0.1)'}`,
                        boxShadow: showNotes ? '0 0 20px rgba(245, 158, 11, 0.4)' : 'none'
                      }}>
                      <FaStickyNote /> Notas ({mealPlan.custom_pages.length})
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {/* Notes section */}
            {showNotes && mealPlan?.custom_pages?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 mb-8">
                <h3 className="text-2xl font-bold text-white uppercase flex items-center gap-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <FaStickyNote style={{ color: '#F59E0B' }} /> Notas Adicionales
                </h3>
                {mealPlan.custom_pages.map((page, index) => (
                  <Card key={page.id || index} className="border-2" style={{ backgroundColor: 'rgba(245, 158, 11, 0.05)', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-lg text-amber-400"><FaFileAlt />{page.title || `Nota ${index + 1}`}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-gray-300 whitespace-pre-wrap leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: (page.content || '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>').replace(/__(.*?)__/g, '<u>$1</u>').replace(/\n/g, '<br/>') }} />
                    </CardContent>
                  </Card>
                ))}
              </motion.div>
            )}

            {/* Meals */}
            {!showNotes && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white uppercase" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Tus Comidas {multiDiets && activeDietTab && DIET_TAB_LABELS[activeDietTab] && `- ${DIET_TAB_LABELS[activeDietTab].name}`}
                </h3>

                {/* 💧 Hidratación diaria */}
                {(() => {
                  const currentDiet = multiDiets?.[activeDietTab] || mealPlan;
                  const waterLiters = currentDiet?.water_liters ?? mealPlan?.water_liters;
                  const waterNote = currentDiet?.water_note ?? mealPlan?.water_note;
                  const hasWater = waterLiters != null && Number(waterLiters) > 0;
                  if (!hasWater) return null;
                  return (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl p-5 backdrop-blur-xl mb-4"
                      style={{ background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.20), rgba(14, 165, 233, 0.10))', border: '2px solid rgba(14, 165, 233, 0.45)' }}
                      data-testid="water-widget">
                      <div className="flex items-center gap-4">
                        <div className="text-4xl leading-none">💧</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] uppercase tracking-widest text-cyan-300/80 mb-1">
                            Hidratación diaria
                          </p>
                          <div className="flex items-baseline gap-2">
                            <span className="text-3xl md:text-4xl font-black" style={{ color: '#67E8F9', fontFamily: 'Poppins, sans-serif' }}>
                              {Number(waterLiters).toLocaleString('es-CO', { maximumFractionDigits: 2 })}
                            </span>
                            <span className="text-lg font-bold text-cyan-200/90">L</span>
                            <span className="text-sm text-cyan-100/70">al día</span>
                          </div>
                          {waterNote && (
                            <p className="text-xs md:text-sm text-cyan-100/80 mt-1.5 leading-snug">
                              {waterNote}
                            </p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })()}

                {/* Fasting supplements */}
                {(() => {
                  const currentDiet = multiDiets?.[activeDietTab] || mealPlan;
                  const fastingSupps = currentDiet?.fasting_supplements || mealPlan?.fasting_supplements || [];
                  if (fastingSupps.length > 0) {
                    return (
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="rounded-2xl p-5 backdrop-blur-xl"
                        style={{ background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(251, 191, 36, 0.1))', border: '2px solid rgba(245, 158, 11, 0.4)' }}>
                        <div className="flex items-center gap-3 mb-3">
                          <FaPills className="text-xl" style={{ color: '#F59E0B' }} />
                          <h4 className="text-lg font-bold uppercase" style={{ color: '#F59E0B', fontFamily: 'Poppins, sans-serif' }}>Suplementos en Ayunas</h4>
                          <span className="text-xs text-amber-400/70">(antes del desayuno)</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {fastingSupps.map((supp, idx) => (
                            <Badge key={idx} className="text-sm px-3 py-1 border-0" style={{ background: 'rgba(245, 158, 11, 0.3)', color: '#FCD34D' }}>{supp}</Badge>
                          ))}
                        </div>
                      </motion.div>
                    );
                  }
                  return null;
                })()}

                {/* Meal cards */}
                {(() => {
                  // Determinar qué comidas mostrar:
                  // 1. Si multi_diets tiene comidas CON items para el tab activo, usar esas
                  // 2. Si no, usar las comidas del plan principal (que vienen formateadas del backend)
                  const multiMeals = multiDiets?.[activeDietTab]?.meals;
                  const mealHasFood = (m) => {
                    if (m.items?.length > 0 || m.foods?.length > 0 || m.alimentos?.length > 0) return true;
                    // También verificar items dentro de options (formato multi-opción)
                    if (m.options?.length > 0) return m.options.some(o => o.items?.length > 0);
                    return false;
                  };
                  const multiHasItems = multiMeals?.length > 0 && multiMeals.some(mealHasFood);
                  const currentMeals = multiHasItems ? multiMeals : (mealPlan.meals || []);
                  const currentDiet = multiDiets?.[activeDietTab] || mealPlan;
                  const preWorkoutSupps = currentDiet?.pre_workout_supplements || mealPlan?.pre_workout_supplements || [];
                  const postWorkoutMeal = currentDiet?.post_workout_meal || mealPlan?.post_workout_meal;
                  const postWorkoutIndex = postWorkoutMeal && postWorkoutMeal !== 'none' && postWorkoutMeal !== 'ninguna' ? parseInt(postWorkoutMeal) - 1 : -1;

                  if (currentMeals.length === 0) {
                    return (
                      <div className="rounded-2xl p-12 text-center backdrop-blur-xl" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <FaUtensils className="text-5xl mx-auto mb-4" style={{ color: 'var(--brand-primary)' }} />
                        <p className="text-gray-400 text-lg">Tu plan esta siendo preparado...</p>
                      </div>
                    );
                  }

                  // Pre-computar compensaciones para el tab activo
                  const compInByMeal = {};
                  const sourcesWithDistribution = {};
                  (compensations || []).forEach(c => {
                    if (c.target_diet_tab === activeDietTab) {
                      const idx = c.target_meal_index;
                      if (!compInByMeal[idx]) compInByMeal[idx] = { calories: 0, protein: 0, carbs: 0, fats: 0 };
                      compInByMeal[idx].calories += Number(c.macros?.calories) || 0;
                      compInByMeal[idx].protein += Number(c.macros?.protein) || 0;
                      compInByMeal[idx].carbs += Number(c.macros?.carbs) || 0;
                      compInByMeal[idx].fats += Number(c.macros?.fats) || 0;
                    }
                    if (c.source_diet_tab === activeDietTab) {
                      sourcesWithDistribution[c.source_meal_index] = true;
                    }
                  });

                  const handleDistributeDeficit = async (sourceTab, sourceMealIdx, deficit) => {
                    // Targets = comidas pendientes del MISMO tab, distintas del origen, no consumidas.
                    const targets = currentMeals
                      .map((_m, i) => i)
                      .filter(i => i !== sourceMealIdx && !consumedKeys.has(`${sourceTab}|${i}`));
                    if (targets.length === 0) {
                      toast.error('No hay comidas pendientes a las que repartir el déficit.');
                      return;
                    }
                    try {
                      const res = await axios.post(`${API}/macro-log/compensations/distribute`, {
                        source_diet_tab: sourceTab, source_meal_index: sourceMealIdx,
                        target_diet_tab: sourceTab, target_meal_indices: targets,
                        macros: deficit,
                      }, { headers: { Authorization: `Bearer ${localStorage.getItem('deisycfit_token') || ''}` } });
                      const per = res.data?.per_meal || {};
                      const parts = [];
                      if (per.protein > 0.5) parts.push(`+${(+per.protein).toFixed(1)}g proteína`);
                      if (per.carbs > 0.5) parts.push(`+${(+per.carbs).toFixed(1)}g carbos`);
                      if (per.fats > 0.5) parts.push(`+${(+per.fats).toFixed(1)}g grasas`);
                      toast.success(`Repartido en ${res.data?.count} comidas (${parts.join(' · ')} c/u).`);
                      await loadCompensations();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'No se pudo distribuir el déficit.');
                    }
                  };

                  const handleUndoDistribution = async (sourceTab, sourceMealIdx) => {
                    try {
                      await axios.delete(`${API}/macro-log/compensations/by-source`, {
                        params: { source_diet_tab: sourceTab, source_meal_index: sourceMealIdx },
                        headers: { Authorization: `Bearer ${localStorage.getItem('deisycfit_token') || ''}` },
                      });
                      toast.success('Reparto deshecho.');
                      await loadCompensations();
                    } catch (e) {
                      toast.error('No se pudo deshacer el reparto.');
                    }
                  };

                  return currentMeals.map((meal, mealIndex) => {
                    const isPostWorkout = mealIndex === postWorkoutIndex;
                    const showPreWorkoutSupps = isPostWorkout && preWorkoutSupps.length > 0 && activeDietTab !== 'descanso';
                    const consumedKey = `${activeDietTab}|${mealIndex}`;
                    const isConsumed = consumedKeys.has(consumedKey);
                    // Solo repartir extras entre las comidas PENDIENTES (no consumidas)
                    const pendingCount = currentMeals.reduce((acc, _m, i) => acc + (consumedKeys.has(`${activeDietTab}|${i}`) ? 0 : 1), 0);
                    const divisor = pendingCount || 1;
                    const extrasPerMeal = {
                      calories: (extrasTotals.calories || 0) / divisor,
                      protein: (extrasTotals.protein || 0) / divisor,
                      carbs: (extrasTotals.carbs || 0) / divisor,
                      fats: (extrasTotals.fats || 0) / divisor,
                    };
                    return (
                      <MealCard key={mealIndex} meal={meal} mealIndex={mealIndex} isPostWorkout={isPostWorkout}
                        showPreWorkoutSupps={showPreWorkoutSupps} preWorkoutSupps={preWorkoutSupps}
                        activeDietTab={activeDietTab} selectedOptions={selectedOptions} setSelectedOptions={setSelectedOptions}
                        extrasPerMeal={extrasPerMeal}
                        isConsumed={isConsumed}
                        onToggleConsumed={() => toggleMealConsumed(activeDietTab, mealIndex)}
                        swapsMap={swapsMap}
                        compIn={compInByMeal[mealIndex]}
                        hasActiveDistribution={!!sourcesWithDistribution[mealIndex]}
                        onDistributeDeficit={handleDistributeDeficit}
                        onUndoDistribution={handleUndoDistribution}
                        canUseRecipeLibrary={canUseRecipeLibrary}
                        onOpenRecipeLibrary={(mi, mt) => setRecipeLibrary({ mealIndex: mi, mealTime: mt })}
                        onSwapItem={(itemIndex, item, currentDisplayMacros, mealTotalMacros, itemsList, optionIndex) => {
                          // Extraer los gramos del item para que el backend pueda recalcular
                          // los macros desde la food_database (corrige planes con macros mal cargados).
                          let itemGrams = 0;
                          const q = String(item.quantity || item.grams || '').trim();
                          const m = q.match(/^(\d+(?:[.,]\d+)?)/);
                          if (m) itemGrams = parseFloat(m[1].replace(',', '.'));
                          // Calcular meal_capacity = mealTotalMacros - suma de OTROS items
                          const sumOthers = (itemsList || []).reduce((acc, it, i) => {
                            if (i === itemIndex) return acc;
                            return {
                              calories: acc.calories + (Number(it.calories) || 0),
                              protein: acc.protein + (Number(it.protein) || 0),
                              carbs: acc.carbs + (Number(it.carbs) || 0),
                              fats: acc.fats + (Number(it.fats ?? it.fat) || 0),
                            };
                          }, { calories: 0, protein: 0, carbs: 0, fats: 0 });
                          const meal_capacity = {
                            calories: Math.max(0, (mealTotalMacros.calories || 0) - sumOthers.calories - (currentDisplayMacros.calories || 0)),
                            protein: Math.max(0, (mealTotalMacros.protein || 0) - sumOthers.protein - (currentDisplayMacros.protein || 0)),
                            carbs: Math.max(0, (mealTotalMacros.carbs || 0) - sumOthers.carbs - (currentDisplayMacros.carbs || 0)),
                            fats: Math.max(0, (mealTotalMacros.fats || 0) - sumOthers.fats - (currentDisplayMacros.fats || 0)),
                          };
                          setSwapDialog({
                            food: item.food_name || item.name,
                            foodCategory: item.category,
                            currentMacros: currentDisplayMacros,
                            currentGrams: itemGrams,
                            mealCapacity: meal_capacity,
                            dietTab: activeDietTab,
                            mealIndex,
                            optionIndex: optionIndex || 0,
                            itemIndex,
                          });
                        }} />
                    );
                  });
                })()}
              </div>
            )}
          </>
        )}

        {/* ===== CLINICAL TAB CONTENT ===== */}
        {mainTab === 'clinical' && hasClinicalData && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Download Clinical PDF */}
            <div className="flex justify-end mb-6">
              <Button onClick={handleDownloadClinicalPDF} disabled={downloadingClinical} data-testid="download-clinical-pdf-btn"
                className="rounded-full px-6 font-bold uppercase tracking-wider text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
                {downloadingClinical ? <><span className="animate-spin mr-2">...</span> Generando...</> : <><FaDownload className="mr-2" /> Descargar Clinico PDF</>}
              </Button>
            </div>

            {/* Clinical Summary Content */}
            <div className="rounded-2xl p-6 backdrop-blur-xl mb-8"
              style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(6, 95, 70, 0.12))', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.4), rgba(5, 150, 105, 0.4))' }}>
                  <FaShieldAlt className="text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Poppins, sans-serif' }} data-testid="clinical-summary-title">
                    Ajustes Clinicos de tu Plan
                  </h3>
                  <p className="text-xs text-emerald-400/70">Plan adaptado a tu perfil de salud</p>
                </div>
              </div>

              {clinicalSummary.summary_intro && (
                <p className="text-gray-300 text-sm leading-relaxed mb-5 pl-1" data-testid="clinical-summary-intro">{clinicalSummary.summary_intro}</p>
              )}

              <div className="space-y-3">
                {/* 0. Conditions */}
                {conditions.length > 0 && (
                  <SectionToggle title="Condiciones Identificadas" icon={FaHeartbeat} iconColor="#f472b6" count={conditions.length} defaultOpen={true} testId="clinical-conditions-toggle">
                    <div className="flex flex-wrap gap-2">
                      {conditions.map((cond, i) => (
                        <Badge key={i} className="text-sm px-3 py-1.5 border-0" style={{ background: 'rgba(244, 114, 182, 0.15)', color: '#f9a8d4' }} data-testid={`clinical-condition-${i}`}>{cond}</Badge>
                      ))}
                    </div>
                  </SectionToggle>
                )}

                {/* 1. Plan de Accion Inicial */}
                {actionPlans.length > 0 && (
                  <SectionToggle title="Plan de Accion Inicial" icon={FaClipboardList} iconColor="#34d399" count={actionPlans.length} defaultOpen={true} testId="clinical-action-plans-toggle">
                    <div className="space-y-3">
                      {actionPlans.map((plan, i) => (
                        <div key={i} className="p-4 rounded-xl" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                          <h5 className="text-white font-semibold text-sm mb-2">{plan.title}</h5>
                          {plan.clinical_objective && (
                            <p className="text-emerald-300/80 text-xs italic mb-3 leading-relaxed">{plan.clinical_objective}</p>
                          )}
                          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1.5">Acciones prioritarias</p>
                          <ul className="space-y-1.5 mb-3">
                            {(plan.actions || []).map((action, j) => (
                              <li key={j} className="flex items-start gap-2 text-gray-300 text-sm"><span className="text-emerald-400 mt-0.5 flex-shrink-0">&#8226;</span>{action}</li>
                            ))}
                          </ul>
                          {(plan.monitoring_points || []).length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Monitoreo sugerido</p>
                              <div className="flex flex-wrap gap-1.5">
                                {plan.monitoring_points.map((mp, j) => (
                                  <Badge key={j} className="text-xs px-2 py-0.5 border-0" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#6ee7b7' }}>{mp}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {plan.follow_up && (
                            <p className="text-xs text-gray-400 mt-2"><span className="text-gray-500 font-medium">Seguimiento:</span> {plan.follow_up}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </SectionToggle>
                )}

                {/* 2. Alimentos a Evitar o Controlar */}
                {avoidFoods.length > 0 && (
                  <SectionToggle title="Alimentos a Evitar o Controlar" icon={FaBan} iconColor="#f87171" count={avoidFoods.length} testId="clinical-avoid-foods-toggle">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {avoidFoods.map((food, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.08)' }}>
                          <span className="text-red-400 text-xs">&#10005;</span>
                          <span className="text-gray-300 text-sm">{food}</span>
                        </div>
                      ))}
                    </div>
                  </SectionToggle>
                )}

                {/* 3. Sustituciones Recomendadas */}
                {substitutions.length > 0 && (
                  <SectionToggle title="Sustituciones Recomendadas" icon={FaExchangeAlt} iconColor="#60a5fa" count={substitutions.length} testId="clinical-substitutions-toggle">
                    <div className="space-y-2">
                      {substitutions.map((sub, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(96, 165, 250, 0.06)' }}>
                          <span className="text-red-400/80 text-sm line-through">{sub.original}</span>
                          <span className="text-gray-500 text-xs">&#8594;</span>
                          <div className="flex flex-wrap gap-1">
                            {(sub.replacements || []).map((rep, j) => (
                              <Badge key={j} className="text-xs px-2 py-0.5 border-0" style={{ background: 'rgba(96, 165, 250, 0.15)', color: '#93c5fd' }}>{rep}</Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </SectionToggle>
                )}

                {/* 4. Suplementacion Recomendada */}
                {recommendedSupplements.length > 0 && (
                  <SectionToggle title="Suplementacion Recomendada" icon={FaShieldAlt} iconColor="#a78bfa" count={recommendedSupplements.length} defaultOpen={true} testId="clinical-recommended-supplements-toggle">
                    <div className="space-y-2">
                      {recommendedSupplements.map((supp, i) => (
                        <div key={i} className="p-3 rounded-lg" style={{ background: 'rgba(167, 139, 250, 0.06)', border: '1px solid rgba(167, 139, 250, 0.12)' }}>
                          <div className="flex items-start justify-between">
                            <div>
                              <span className="text-white text-sm font-medium">{supp.name}</span>
                              {supp.dose && <span className="text-purple-300 text-xs ml-2">({supp.dose})</span>}
                            </div>
                            {supp.timing && (
                              <Badge className="text-xs px-2 py-0 border-0" style={{ background: 'rgba(167, 139, 250, 0.2)', color: '#c4b5fd' }}>{supp.timing}</Badge>
                            )}
                          </div>
                          {supp.reason && <p className="text-gray-500 text-xs mt-1">{supp.reason}</p>}
                        </div>
                      ))}
                    </div>
                  </SectionToggle>
                )}

                {/* 5. Suplementos a Evitar / Usar con Precaucion */}
                {avoidSupplements.length > 0 && (
                  <SectionToggle title="Suplementos a Evitar o Usar con Precaucion" icon={FaBan} iconColor="#fb923c" count={avoidSupplements.length} defaultOpen={true} testId="clinical-avoid-supplements-toggle">
                    <div className="space-y-2">
                      {avoidSupplements.map((supp, i) => (
                        <div key={i} className="flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(251, 146, 60, 0.06)', border: '1px solid rgba(251, 146, 60, 0.12)' }}>
                          <span className="text-orange-400 text-xs mt-0.5 flex-shrink-0">&#9888;</span>
                          <div>
                            <span className="text-white text-sm font-medium">{supp.name}</span>
                            {supp.reason && <p className="text-gray-500 text-xs mt-1">{supp.reason}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </SectionToggle>
                )}

                {/* 6. Recomendaciones / Notas Adicionales */}
                {recommendations.length > 0 && (
                  <SectionToggle title="Notas Clinicas Adicionales" icon={FaLightbulb} iconColor="#fbbf24" count={recommendations.length} testId="clinical-recommendations-toggle">
                    <div className="space-y-2">
                      {recommendations.map((rec, i) => (
                        <div key={i} className="p-3 rounded-lg" style={{ background: 'rgba(251, 191, 36, 0.06)', borderLeft: '3px solid rgba(251, 191, 36, 0.4)' }}>
                          <p className="text-gray-300 text-sm leading-relaxed">{rec}</p>
                        </div>
                      ))}
                    </div>
                  </SectionToggle>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Diálogo global para cambiar un alimento de la comida */}
      {swapDialog && (
        <SwapFoodDialog
          open={!!swapDialog}
          onOpenChange={(open) => { if (!open) setSwapDialog(null); }}
          food={swapDialog.food}
          foodCategory={swapDialog.foodCategory}
          currentMacros={swapDialog.currentMacros}
          currentGrams={swapDialog.currentGrams}
          mealCapacity={swapDialog.mealCapacity}
          dietTab={swapDialog.dietTab}
          mealIndex={swapDialog.mealIndex}
          optionIndex={swapDialog.optionIndex || 0}
          itemIndex={swapDialog.itemIndex}
          hasExistingSwap={!!swapsMap[`${swapDialog.dietTab}|${swapDialog.mealIndex}|${swapDialog.optionIndex || 0}|${swapDialog.itemIndex}`]}
          today={new Date().toISOString().split('T')[0]}
          onSaved={async () => { await loadSwaps(); await loadCompensations(); }}
        />
      )}
      {/* 2026-02 · Biblioteca de recetas del usuario (asesoría 1 a 1) */}
      {recipeLibrary && (
        <RecipeLibraryModal
          isOpen={!!recipeLibrary}
          onClose={() => setRecipeLibrary(null)}
          mealIndex={recipeLibrary.mealIndex}
          mealTime={recipeLibrary.mealTime}
          dayType={activeDietTab}
          onAdded={() => { fetchData(); }}
        />
      )}
    </div>
  );
};

export default MyDiet;
